import React, { useMemo, useState } from 'react'
import { Modal } from '../../../map-editor-shared/ui/Modal'
import type {
  ApplyResult,
  ValidatedRecord,
  ValidationResult,
} from '../model/types'
import { ImportSummary } from './components/ImportSummary'
import { ImportFilterTabs, type ImportFilter } from './components/ImportFilterTabs'
import { ImportRecordTable } from './components/ImportRecordTable'
import { ImportDiffPreview } from './components/ImportDiffPreview'
import { EmptyOrSkippedBanner } from './components/EmptyOrSkippedBanner'
import styles from './ExcelImportModal.module.css'

interface Props {
  isOpen: boolean
  onClose: () => void
  result: ValidationResult | null
  fileName?: string
  /**
   * Sheets present in the workbook but not matched to any active ObjectType.
   * Surfaced as a warning banner so the user notices silent drops.
   */
  skippedSheets?: string[]
  /** Caller is responsible for executing the apply (so the host owns the graph). Returns counts. */
  onApply: (result: ValidationResult) => ApplyResult | Promise<ApplyResult>
}

/**
 * Validation result viewer + apply gate.
 *
 * The modal does not run validation itself — it consumes a precomputed
 * `ValidationResult` so the host can choose when/how to invoke the parser
 * + validator chain.
 */
export const ExcelImportModal: React.FC<Props> = ({
  isOpen,
  onClose,
  result,
  fileName,
  skippedSheets = [],
  onApply,
}) => {
  const [filter, setFilter] = useState<ImportFilter>('all')
  const [selectedRowKey, setSelectedRowKey] = useState<string | null>(null)
  const [applying, setApplying] = useState(false)

  const filteredRecords = useMemo<ValidatedRecord[]>(() => {
    if (!result) return []
    if (filter === 'all') return result.records
    return result.records.filter(r => r.status === filter)
  }, [result, filter])

  const selectedRecord = useMemo<ValidatedRecord | null>(() => {
    if (!result || !selectedRowKey) return null
    return (
      result.records.find(
        r => `${r.record.sheetName}-${r.record.rowNumber}` === selectedRowKey
      ) ?? null
    )
  }, [result, selectedRowKey])

  const handleApply = async () => {
    if (!result || result.hasBlockingErrors) return
    setApplying(true)
    try {
      await onApply(result)
      onClose()
    } finally {
      setApplying(false)
    }
  }

  if (!result) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title="Excel 업로드">
        <div className={styles.loading}>파싱 및 검증 중…</div>
      </Modal>
    )
  }

  const applyDisabled = applying || result.hasBlockingErrors || result.summary.valid === 0

  const footer = (
    <div className={styles.footer}>
      {result.summary.unmatched > 0 && (
        <span className={styles.warning}>
          ⚠ 미매칭 {result.summary.unmatched}건은 적용에서 제외됩니다.
        </span>
      )}
      <div className={styles.footerActions}>
        <button
          type="button"
          className={styles.cancelBtn}
          onClick={onClose}
          disabled={applying}
        >
          취소
        </button>
        <button
          type="button"
          className={styles.applyBtn}
          onClick={handleApply}
          disabled={applyDisabled}
          title={
            result.hasBlockingErrors
              ? '오류가 있는 레코드를 수정한 후 다시 업로드하세요.'
              : undefined
          }
        >
          {applying ? '적용 중…' : `✓ 적용 (${result.summary.valid}건)`}
        </button>
      </div>
    </div>
  )

  return (
    <Modal
      isOpen={isOpen}
      onClose={applying ? () => {} : onClose}
      title="Excel 업로드"
      footer={footer}
      maxWidth={920}
    >
      <EmptyOrSkippedBanner
        empty={result.summary.total === 0}
        skippedSheets={skippedSheets}
      />
      <ImportSummary summary={result.summary} fileName={fileName} />
      <ImportFilterTabs
        active={filter}
        summary={result.summary}
        onChange={f => {
          setFilter(f)
          setSelectedRowKey(null)
        }}
      />
      <div className={styles.split}>
        <div className={styles.tableArea}>
          <ImportRecordTable
            records={filteredRecords}
            selectedRowKey={selectedRowKey}
            onSelect={setSelectedRowKey}
          />
        </div>
        <div className={styles.previewArea}>
          <ImportDiffPreview record={selectedRecord} />
        </div>
      </div>
    </Modal>
  )
}
